import React, { useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight } from 'lucide-react';

// (Relative URLs are used for all fetches — no REACT_APP_BACKEND_URL needed.)

export function CanonicalSyncBanner({ onClickSync }) {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Relative URL — same-origin fetch, no CORS / stale-URL issues
        const res = await fetch(`/api/admin/migrate/status`, { credentials: 'include' });
        if (!res.ok) return;
        const d = await res.json();
        if (!cancelled) setStatus(d);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!status || dismissed) return null;
  const promptUpgrades = status.prompt_versions_upgrade_count || 0;
  const stale = status.rubric_needs_upgrade || status.system_cases_needs_refresh || promptUpgrades > 0;
  if (!stale) return null;

  const parts = [];
  if (status.rubric_needs_upgrade) {
    parts.push(`rubric ${status.current_rubric_version || 'missing'} → ${status.target_rubric_version}`);
  }
  if (status.system_cases_needs_refresh) {
    parts.push(`system cases ${status.system_cases_current} → ${status.system_cases_target}`);
  }
  if (promptUpgrades > 0) {
    const roles = (status.prompt_versions_need_upgrade || []).join(', ');
    parts.push(`prompts: ${roles}`);
  }

  return (
    <div
      className="mb-4 flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-amber-700/50 bg-amber-900/20"
      data-testid="canonical-sync-banner"
    >
      <div className="flex items-center gap-3 min-w-0">
        <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-amber-200">
            Canonical data is stale
          </div>
          <div className="text-xs text-amber-300/80 mt-0.5 truncate">
            {parts.join(' · ')} — user data preserved. Click to sync.
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={onClickSync}
          className="px-3 py-1.5 text-xs font-medium rounded bg-amber-500 text-black hover:bg-amber-400 transition-colors flex items-center gap-1"
          data-testid="canonical-sync-banner-action"
        >
          Sync now <ArrowRight className="w-3 h-3" />
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="text-amber-300/60 hover:text-amber-200 text-xs px-2"
          aria-label="Dismiss"
          data-testid="canonical-sync-banner-dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
